import { EXPERIMENTS } from '@src/experiments'
import { DeliveryDateRange, DeliveryOptions } from '@src/quince-site-common/src/domains/ProductDeliveryDetails'
import useExperimentBucket from '@src/quince-site-common/src/hooks/useExperimentBucket'
import { useTrackIsElementVisible } from '@src/quince-site-common/src/hooks/useTrackIsElementVisible'
import { useZipCode, useZipCodeEditModal } from '@src/quince-site-common/src/hooks/zipcode'
import zipCodeService from '@src/quince-site-common/src/services/zipCodeService'
import { AllProductsArrivalDetailsType } from '@src/quince-site-common/src/services/zipCodeService/zipCodeService.types'
import {
  getFormattedValentineDate,
  isValentinePhase2orHigher,
  isValentinePhase4,
} from '@src/quince-site-common/src/utils/valentineUtil'
import classNames from 'classnames'
import { useCallback, useEffect, useState } from 'react'
import { getDeliveryInformationForSelection } from './ProductArrivalMessage.helpers'
import * as style from './ProductArrivalMessage.module.scss'

export type ProductIdVsVariantIdMap = Record<number, number[]>

const currentDateTimeInUTC = Date.parse(new Date().toISOString())
const isPhase2orHigher = isValentinePhase2orHigher(currentDateTimeInUTC)
const isPhase4 = isValentinePhase4(currentDateTimeInUTC)

const SHOW_DELIVERY_MESSAGE = isPhase2orHigher
const SHOW_ARRIVAL_FOR_EXPRESS = isPhase4

const ZipcodeContainer = ({ className }: { className?: string }) => {
  const { openZipcodeModal } = useZipCodeEditModal()
  const { zipCode } = useZipCode()

  const handleClick = () => {
    openZipcodeModal(true)
  }

  return (
    <button onClick={handleClick}>
      <span className={classNames(style.zipcodeTextClass, className)}>{zipCode || 'Enter your zip'}</span>
      {!zipCode && ' to see when it will arrive.'}
    </button>
  )
}

interface ProductArrivalMessageContentProps {
  deliveryInformation: {
    standardDeliveryPossible: boolean
    expressDeliveryPossible: boolean
    standardDeliveryDate?: DeliveryDateRange
    expressDeliveryDate?: DeliveryDateRange
  } | null
  deliveryOption: DeliveryOptions
  zipCode?: string
}

const EXPRESS_DELIVERY_MESSAGE = 'Select Express shipping at checkout.'

const getDeliveryMessage = (isExpress: boolean, date?: DeliveryDateRange) => {
  const minDeliveryDate = getFormattedValentineDate(date?.minimum)
  const maxDeliveryDate = getFormattedValentineDate(date?.maximum)
  if (isExpress) {
    if (minDeliveryDate && maxDeliveryDate) {
      return (
        <>
          Arrives between {minDeliveryDate} - {maxDeliveryDate} to{' '}
          <ZipcodeContainer className={style.greeenHighlight} />.{' '}
          <span className={style.defaultText}>{EXPRESS_DELIVERY_MESSAGE}</span>
        </>
      )
    } else {
      return (
        <>
          Arrives before V-Day to <ZipcodeContainer className={style.greeenHighlight} />.{' '}
          <span className={style.defaultText}>{EXPRESS_DELIVERY_MESSAGE}</span>
        </>
      )
    }
  } else {
    if (minDeliveryDate && maxDeliveryDate) {
      return (
        <>
          Arrives between {minDeliveryDate} - {maxDeliveryDate}.{' '}
          <span className={style.defaultText}>
            Ship to <ZipcodeContainer />
          </span>
        </>
      )
    } else {
      return (
        <>
          Arrives before Valentine’s Day.{' '}
          <span className={style.defaultText}>
            Ship to <ZipcodeContainer />
          </span>
        </>
      )
    }
  }
}

const ProductArrivalMessageContent = ({
  deliveryInformation,
  deliveryOption,
  zipCode,
}: ProductArrivalMessageContentProps) => {
  const vdayCutoffBucket = useExperimentBucket(EXPERIMENTS.VDAY_CUTOFF_MESSAGE.experimentName)
  const isExpress = deliveryOption === DeliveryOptions.EXPRESS

  const deliveryPossibleByCutoff = isExpress
    ? deliveryInformation?.expressDeliveryPossible
    : deliveryInformation?.standardDeliveryPossible

  const deliveryDate = isExpress ? deliveryInformation?.expressDeliveryDate : deliveryInformation?.standardDeliveryDate

  const showDeliveryDate = vdayCutoffBucket === EXPERIMENTS.VDAY_CUTOFF_MESSAGE.bucketNames.showDate

  const getIsVisibleTrackParams = useCallback(
    () => ({
      action: 'zip_code',
      value: {
        zipCode: zipCode,
        isExpress: isExpress,
        deliveryPossible: deliveryPossibleByCutoff,
        deliveryDate: deliveryDate ? `${deliveryDate.minimum} - ${deliveryDate.maximum}` : undefined,
      },
    }),
    [],
  )
  const { ref: trackIsVisibleRef } = useTrackIsElementVisible(getIsVisibleTrackParams)

  if (vdayCutoffBucket === EXPERIMENTS.VDAY_CUTOFF_MESSAGE.bucketNames.control) {
    return null
  }

  if (!zipCode) {
    return (
      <div className={style.wrapper} ref={trackIsVisibleRef}>
        <span className={style.greeenHighlight}>
          <ZipcodeContainer />
        </span>
      </div>
    )
  }

  if (deliveryInformation && deliveryPossibleByCutoff) {
    return (
      <div className={style.wrapper} ref={trackIsVisibleRef}>
        <span className={style.greeenHighlight}>
          {getDeliveryMessage(isExpress, showDeliveryDate ? deliveryDate : undefined)}
        </span>
      </div>
    )
  }
  return null
}

function ProductArrivalMessage({
  allProductIdsVsVariantIdsMap,
  selectedProductIdsVsVariantIdsMap,
}: {
  allProductIdsVsVariantIdsMap: ProductIdVsVariantIdMap
  selectedProductIdsVsVariantIdsMap: ProductIdVsVariantIdMap
}) {
  const [allProductsArrivalDetails, setAllProductsArrivalDetails] = useState<AllProductsArrivalDetailsType | null>(null)

  const [isLoading, setIsLoading] = useState(true)
  const { zipCode } = useZipCode()

  const deliveryInformation = allProductsArrivalDetails
    ? getDeliveryInformationForSelection(selectedProductIdsVsVariantIdsMap, allProductsArrivalDetails, zipCode)
    : null

  const getDynamicContents = async () => {
    setIsLoading(true)
    if (zipCode) {
      try {
        const _allProductsArrivalDetails = await zipCodeService.getPDPDynamicContents(
          zipCode,
          allProductIdsVsVariantIdsMap,
        )
        setAllProductsArrivalDetails(_allProductsArrivalDetails)
      } catch (error) {
        console.error('Error while fetching dynamicContents', error)
      }
    }
    setIsLoading(false)
  }

  useEffect(() => {
    getDynamicContents().catch((error: unknown) => {
      console.error('Error fetching getDynamicContents', error)
    })
  }, [zipCode, allProductIdsVsVariantIdsMap])

  return isLoading || !SHOW_DELIVERY_MESSAGE ? null : (
    <ProductArrivalMessageContent
      deliveryInformation={deliveryInformation}
      deliveryOption={SHOW_ARRIVAL_FOR_EXPRESS ? DeliveryOptions.EXPRESS : DeliveryOptions.STANDARD}
      zipCode={zipCode}
    />
  )
}

export default ProductArrivalMessage
